/**
 * The outdoor alarm, its kind, and the perimeter one kind needs.
 *
 * The mode a carer picked and the mode that applies are not the same value. Two things are not
 * theirs to decide — an allow list forces the alarm on, and a perimeter needs GPS the device may
 * not have — so the applied mode is derived rather than written into state. Keeping their own last
 * answer means switching away from an allow list gives it back, instead of silently keeping one
 * nobody chose.
 */

import * as React from 'react';
import { DeviceType, OutdoorGeofencingMode, ProfileConfiguration } from '@ixicare/resources';
import GeoJSON from 'geojson';
import UserProfileEntry from '~store/Profile/UserProfileEntry';

export interface OutdoorGeofencing {
  mode: OutdoorGeofencingMode;
  setMode: (next: OutdoorGeofencingMode) => void;
  on: boolean;
  isProDevice: boolean;
  geo?: GeoJSON.Feature<GeoJSON.Polygon>;
  setGeo: (next?: GeoJSON.Feature<GeoJSON.Polygon>) => void;
  hasPerimeter: boolean;
}

export const useOutdoorGeofencing = (
  configuration: ProfileConfiguration | undefined,
  profile: UserProfileEntry,
  requiredByIndoor: boolean
): OutdoorGeofencing => {
  // Held out of the reducer bag the save spreads back, because the map owns it.
  const [geo, setGeo] = React.useState<GeoJSON.Feature<GeoJSON.Polygon> | undefined>(
    configuration?.outdoorGeofencingGeo
  );
  const [chosen, setMode] = React.useState<OutdoorGeofencingMode>(
    configuration?.outdoorGeofencingMode ?? OutdoorGeofencingMode.None
  );

  const isProDevice =
    profile.ownedDevice?.deviceType === DeviceType.WATCHERR || profile.ownedDevice?.deviceType === DeviceType.IXI_PRO;

  const mode = React.useMemo(() => {
    let next = chosen;
    if (requiredByIndoor && next === OutdoorGeofencingMode.None) next = OutdoorGeofencingMode.Gateway;
    // A perimeter needs GPS. A device without it gets the contact-loss alarm instead, never nothing.
    if (!isProDevice && next === OutdoorGeofencingMode.Geo) next = OutdoorGeofencingMode.Gateway;
    return next;
  }, [chosen, requiredByIndoor, isProDevice]);

  return {
    mode,
    setMode,
    on: mode !== OutdoorGeofencingMode.None,
    isProDevice,
    geo,
    setGeo,
    // An empty object comes back from the perimeter editor when nothing has been drawn yet.
    hasPerimeter: Boolean(geo && Object.keys(geo).length > 0),
  };
};
